/**
 * Life4Earth V2 — Projects API Adapter
 *
 * Replaces: seed data from Data/projects.ts + localStorage overrides
 *           from Data/ProjectConfig/projectConfigOverrides.ts
 * Endpoint: /api/Projects
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types ────────────────────────────────────────────────────

export interface ProjectResponse {
  id: string;
  name: string;
  description: string | null;
  status: string;
  region: string | null;
  communityId: string | null;
  projectCategory: string | null;
  projectStatus: string | null;
  contentStatus: string | null;
  projectReach: string | null;
  startDate: string | null;
  finishDate: string | null;
  budgetEstimate: string | null;
  budgetCurrency: string | null;
  contingencyPercent: number | null;
  locationLat: string | null;
  locationLon: string | null;
  communityL1: string | null;
  communityL2: string | null;
  communityL3: string | null;
  communityDisplay: string | null;
  primaryLocationEntityGuid: string | null;
  /**
   * DEV-877: org-root community_entity that owns this project, derived
   * server-side from walk_org_root(community_id). Read-only on the wire.
   * Null only for legacy rows whose community_id has no org-root ancestor.
   * Used by the collection-add eligibility filter.
   */
  primaryOwnerOrgGuid: string | null;
  /**
   * DEV-912: immutable founder. Set once on project creation; never
   * modified by any update path. No UI consumes this yet — present on
   * the wire so future Launch 2/3 work (founder display on profile,
   * "projects I founded" lists, succession workflows) reads it without
   * an extra roundtrip. NULL only for legacy rows whose `created_by`
   * was also unset at backfill time.
   */
  originatedByUserGuid: string | null;
  /**
   * DEV-916: references collection_schemas.schema_guid. Determines which
   * custom metadata fields the Project Settings Metadata tab renders.
   * NULL = no schema chosen; tab is hidden. Round-tripped through
   * `toSeedShape` as `metadata_schema_guid` for the modal's read-back path.
   */
  metadataSchemaGuid: string | null;
  heroImageUrl: string | null;
  cardImageUrl: string | null;
  logoUrl: string | null;
  reportingCadence: string | null;
  // DEV-814: round-trip CategoryWeights for the Summary KPI rollup.
  // Stored server-side as jsonb, sent over the wire as a JSON string.
  categoryWeights: string | null;

  /**
   * DEV-1463 (migration 275) — the two visibility dials, mirroring
   * mini-projects. 'team_only' | 'community' | 'public'.
   *
   * ⚠ ADVISORY until DEV-1071 lands the server gate. `GET /api/projects` still
   *   returns every project to every caller — these fields do NOT yet mean the
   *   list has been filtered. The Discover catalog uses them for
   *   DISCOVERABILITY (what is worth showing); DEV-1071 will use them for
   *   READABILITY (what you are permitted to see). Different jobs.
   *
   * ⚠ DO NOT feed `visibility` into the V2 permission evaluator yet. It expects
   *   a two-value space ("public" | "private") and has a dormant Rule 5 that
   *   grants every authenticated user the cascadable view set on any project
   *   marked public. Since migration 275 backfills every existing project to
   *   'public', wiring this through would fire Rule 5 platform-wide and flood
   *   every user's Portfolio with every project. See the pin in App.tsx.
   */
  visibility: string | null;
  progressVisibility: string | null;

  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  region?: string;
  communityId?: string;
  projectCategory?: string;
  projectStatus?: string;
  contentStatus?: string;
  projectReach?: string;
  startDate?: string;
  finishDate?: string;
  budgetEstimate?: string;
  budgetCurrency?: string;
  contingencyPercent?: number;
  locationLat?: string;
  locationLon?: string;
  communityL1?: string;
  communityL2?: string;
  communityL3?: string;
  communityDisplay?: string;
  primaryLocationEntityGuid?: string;
  heroImageUrl?: string;
  cardImageUrl?: string;
  logoUrl?: string;
  reportingCadence?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  status?: string;
  region?: string;
  communityId?: string;
  projectCategory?: string;
  projectStatus?: string;
  contentStatus?: string;
  projectReach?: string;
  startDate?: string;
  finishDate?: string;
  budgetEstimate?: string;
  budgetCurrency?: string;
  contingencyPercent?: number;
  locationLat?: string;
  locationLon?: string;
  communityL1?: string;
  communityL2?: string;
  communityL3?: string;
  communityDisplay?: string;
  primaryLocationEntityGuid?: string;
  heroImageUrl?: string;
  cardImageUrl?: string;
  logoUrl?: string;
  reportingCadence?: string;
  // DEV-814: JSON string of category weights (e.g. '{"schedule":25,"finance":25,"mission":30,"data_reliability":20}').
  // setProjectOverride converts the frontend object to this string before send.
  categoryWeights?: string;
}

/**
 * Convert an API ProjectResponse to the seed-data shape
 * the existing React pipeline expects (project_GUID, project_name, etc.).
 * This is the bridge that makes the API a drop-in replacement for
 * getAllProjectsSeedPlusAdded().
 */
export function toSeedShape(p: ProjectResponse): Record<string, any> {
  return {
    project_GUID: normalizeGuid(p.id),
    project_name: p.name,
    project_category: p.projectCategory ?? "",
    project_status: p.projectStatus ?? "In Progress",
    content_status: p.contentStatus ?? "",
    project_reach: p.projectReach ?? "",
    start_date: p.startDate ?? "",
    finish_date: p.finishDate ?? "",
    project_start_date: p.startDate ?? "",
    project_end_date: p.finishDate ?? "",
    budget_estimate: p.budgetEstimate ?? "",
    budget_currency: p.budgetCurrency ?? "USD",
    contingency_percent: p.contingencyPercent ?? 10,
    location_lat: p.locationLat ?? "",
    location_lon: p.locationLon ?? "",
    community_l1: p.communityL1 ?? "",
    community_l2: p.communityL2 ?? "",
    community_l3: p.communityL3 ?? "",
    Community: p.communityDisplay ?? p.communityL3 ?? "",
    primary_location_entity_guid: normalizeGuid(p.primaryLocationEntityGuid),
    // DEV-913 round-trip: expose community_id as owning_community_entity_guid
    // so the Project Settings modal can derive owner_type='community' on
    // read-back. The modal's owner-deriving logic (ProjectConfigModal.tsx
    // line ~846) already reads this legacy field; without it, every project
    // loads as "owned by Me" regardless of what the DB actually stores,
    // and any owner change reverts on the next render.
    owning_community_entity_guid: normalizeGuid(p.communityId),
    // Also keep the canonical name available — same value, different key,
    // for any consumers that read the camelCase / snake_case spelling
    // directly. Cheap insurance against future readers diverging.
    community_id: normalizeGuid(p.communityId),
    // DEV-916 round-trip: expose metadata_schema_guid so the Project Settings
    // modal can re-populate the schema dropdown on read-back. The modal reads
    // `project?.metadata_schema_guid` (ProjectConfigModal.tsx line ~952) at
    // open time; without this, every project loads with no schema selected
    // and the Metadata tab is invisible.
    metadata_schema_guid: normalizeGuid(p.metadataSchemaGuid),
    // Extra fields the pipeline may reference
    description: p.description ?? "",
    region: p.region ?? "",
    // Image URLs (from MinIO or external)
    hero_image_url: p.heroImageUrl ?? "",
    tile_image_url: p.heroImageUrl ?? "",   // alias used by override layer
    card_image_url: p.cardImageUrl ?? "",   // card-specific thumbnail (overrides hero on cards)
    logo_url: p.logoUrl ?? "",
    reporting_cadence: p.reportingCadence ?? "",
    // DEV-814: parse the JSON string to an object so callers can read
    // weights as `project.category_weights.schedule` etc. Falls through
    // to undefined on parse failure; consumers must handle the absence.
    category_weights: parseCategoryWeights(p.categoryWeights),

    /* ── DEV-1463 — the project visibility standard (migration 275) ──────────
     *
     * Canonical three-value ladder, straight from the DB column. Skip, 2026-07-13:
     * "the mini project approach is what I want to implement on full projects so
     * we have a project standard."
     *
     * This SUPERSEDES the DEV-1039 pair (visibility: public|private +
     * public_scope: platform|within_community), which encoded the same ladder
     * across two fields and is now retired. `public_scope` is no longer emitted.
     *
     * It also makes the setting WORK FOR THE FIRST TIME: DEV-1039's value was
     * never persisted — the override store hydrates from the API only, and
     * `toSeedShape` could not emit `visibility` because no column existed. The
     * control was session-only and silently reset to "Public" on every reload.
     *
     * ⚠ App.tsx's `v2_projectsForEval` reads this key and is PINNED — it must
     *   stay pinned. Migration 275 backfilled every existing project to 'public',
     *   so un-pinning would fire the V2 evaluator's dormant Rule 5 platform-wide
     *   (grant every authenticated user view on everything). Reconciling the
     *   evaluator's two-value model with this three-value one is DEV-1071's job.
     */
    visibility: p.visibility ?? "team_only",
    progress_visibility: p.progressVisibility ?? "team_only",
  };
}

/**
 * Parse a CategoryWeights JSON string from the API.
 *
 * Returns undefined when the input is null/empty/malformed — callers fall
 * back to DEFAULT_CATEGORY_WEIGHTS in that case. We intentionally don't
 * throw on bad JSON; a corrupted weights blob shouldn't break the entire
 * project load.
 */
function parseCategoryWeights(raw: string | null | undefined): Record<string, number> | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    if (typeof console !== "undefined") {
      console.warn("[projects] CategoryWeights failed to parse; falling back to defaults", raw);
    }
    return undefined;
  }
}

// ── API Functions ────────────────────────────────────────────

export async function fetchProjects(
  status: string = "active"
): Promise<ProjectResponse[]> {
  return apiFetch<ProjectResponse[]>(
    `/api/Projects?status=${encodeURIComponent(status)}`
  );
}

export async function fetchAllProjects(): Promise<ProjectResponse[]> {
  return apiFetch<ProjectResponse[]>(`/api/Projects?status=`);
}

/** Fetch projects belonging to a specific community (all statuses). */
export async function fetchProjectsByCommunity(
  communityId: string
): Promise<ProjectResponse[]> {
  return apiFetch<ProjectResponse[]>(
    `/api/Projects?status=&communityId=${encodeURIComponent(communityId)}`
  );
}

export async function fetchProjectById(
  id: string
): Promise<ProjectResponse> {
  return apiFetch<ProjectResponse>(`/api/Projects/${id}`);
}

export async function createProject(
  request: CreateProjectRequest
): Promise<ProjectResponse> {
  return apiFetch<ProjectResponse>("/api/Projects", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function updateProject(
  id: string,
  request: UpdateProjectRequest
): Promise<ProjectResponse> {
  return apiFetch<ProjectResponse>(`/api/Projects/${id}`, {
    method: "PUT",
    body: JSON.stringify(request),
  });
}

/**
 * POLICY_PROJECT_OWNERSHIP_TRANSFER.md Phase 2/3 — transfer the Full Project
 * to a different organization community. The caller (project owner) must
 * hold community:admin on the destination (auto-commit) or
 * community:create_projects (Phase 3 approval — currently returns 403
 * approval_required).
 *
 * On 403, the thrown ApiError carries body { error, message } so callers
 * can branch on `err.body.error === 'approval_required'` to show a clear
 * message rather than a generic failure.
 */
export async function transferProjectOwnership(
  id: string,
  toCommunityGuid: string,
): Promise<ProjectResponse> {
  return apiFetch<ProjectResponse>(`/api/Projects/${id}/transfer-ownership`, {
    method: "POST",
    body: JSON.stringify({ toCommunityGuid }),
  });
}

export async function archiveProject(id: string): Promise<void> {
  return apiFetch<void>(`/api/Projects/${id}`, {
    method: "DELETE",
  });
}

export interface HardDeleteResult {
  message: string;
  projectId: string;
  recordsRemoved: Record<string, number>;
}

/**
 * Permanently delete a project and ALL child data.
 * Admin only. Irreversible.
 */
export async function hardDeleteProject(id: string): Promise<HardDeleteResult> {
  return apiFetch<HardDeleteResult>(`/api/Projects/${id}/hard`, {
    method: "DELETE",
  });
}

/**
 * Fetch projects from the API and return them in seed-data shape.
 * Drop-in replacement for getAllProjectsSeedPlusAdded().
 */
export async function fetchProjectsAsSeedShape(): Promise<Record<string, any>[]> {
  const projects = await fetchProjects("active");
  return projects.map(toSeedShape);
}
